import { useEffect, useRef } from 'react'
import axios from 'axios'

const MAX_ROUTE_LENGTH = 2048
const MAX_PATHNAME_LENGTH = 512
const MAX_SEARCH_LENGTH = 2048
const MAX_HASH_LENGTH = 512
const MAX_RETRY_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 1000

const normalizeValue = (value, maxLength) => {
  if (typeof value !== 'string') return ''
  return value.slice(0, maxLength)
}

const getRouteDetails = (location) => {
  const pathname = normalizeValue(
    location?.pathname || window?.location?.pathname || '',
    MAX_PATHNAME_LENGTH,
  )
  const search = normalizeValue(
    location?.search || window?.location?.search || '',
    MAX_SEARCH_LENGTH,
  )
  const hash = normalizeValue(
    location?.hash || window?.location?.hash || '',
    MAX_HASH_LENGTH,
  )
  const route = normalizeValue(
    `${pathname}${search}${hash}`,
    MAX_ROUTE_LENGTH,
  )

  return {
    route,
    pathname,
    search,
    hash,
  }
}

const logRouteActivity = async (payload) => {
  await axios.post('/api/user/activity/route', payload, {
    headers: { Pragma: 'no-cache' },
    withCredentials: true,
  })
}

const getRetryDelayMs = (attempt) => RETRY_BASE_DELAY_MS * 2 ** attempt

const clearRetryTimeouts = (retryTimeoutsRef) => {
  retryTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId))
  retryTimeoutsRef.current.clear()
}

const logRouteActivityWithRetry = ({ payload, retryTimeoutsRef, attempt = 0 }) => {
  logRouteActivity(payload).catch((error) => {
    if (attempt >= MAX_RETRY_ATTEMPTS) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[RouteActivity] Failed to log route activity', error)
      }
      return
    }

    const timeoutId = setTimeout(() => {
      retryTimeoutsRef.current.delete(timeoutId)
      logRouteActivityWithRetry({
        payload,
        retryTimeoutsRef,
        attempt: attempt + 1,
      })
    }, getRetryDelayMs(attempt))
    retryTimeoutsRef.current.add(timeoutId)
  })
}

const shouldSkipRoute = (route) => !route

const shouldSkipDuplicateRoute = (previousRoute, nextRoute) =>
  previousRoute === nextRoute

const shouldSkipAuthFreeRoute = (pathname) =>
  pathname === '/reset-password' || pathname === '/brand-hub'

const buildRoutePayload = ({
  nextRoute,
  nextPathname,
  nextSearch,
  nextHash,
  previousRoute,
  previousPathname,
  isInitial,
}) => ({
  toRoute: nextRoute,
  toPathname: nextPathname,
  toSearch: nextSearch || null,
  toHash: nextHash || null,
  fromRoute: previousRoute || null,
  fromPathname: previousPathname || null,
  isInitial,
})

/**
 * Logs route visits while allowing callers to inject route-specific skip rules.
 *
 * Problem: the logger is shared infrastructure, so product-specific route
 * policies need to be passed in rather than imported directly here.
 *
 * @param {Object} params - hook inputs
 * @param {boolean} params.enabled - whether authenticated route logging is active
 * @param {Object} params.location - react-router location object
 * @param {Function} [params.shouldSkipRouteActivity] - optional domain-specific skip predicate
 * @returns {void}
 */
export default function useRouteActivityLogger({
  enabled,
  location,
  shouldSkipRouteActivity,
}) {
  const previousRouteRef = useRef(null)
  const previousPathnameRef = useRef(null)
  const hasLoggedRef = useRef(false)
  const retryTimeoutsRef = useRef(new Set())

  useEffect(() => () => clearRetryTimeouts(retryTimeoutsRef), [])

  useEffect(() => {
    if (!enabled) {
      clearRetryTimeouts(retryTimeoutsRef)
      previousRouteRef.current = null
      previousPathnameRef.current = null
      hasLoggedRef.current = false
      return
    }

    const { route, pathname, search, hash } = getRouteDetails(location)

    if (shouldSkipRoute(route) || shouldSkipAuthFreeRoute(pathname)) {
      return
    }

    if (shouldSkipDuplicateRoute(previousRouteRef.current, route)) {
      return
    }

    if (shouldSkipRouteActivity?.({
      nextPathname: pathname,
      previousPathname: previousPathnameRef.current,
    })) {
      return
    }

    const payload = buildRoutePayload({
      nextRoute: route,
      nextPathname: pathname,
      nextSearch: search,
      nextHash: hash,
      previousRoute: previousRouteRef.current,
      previousPathname: previousPathnameRef.current,
      isInitial: !hasLoggedRef.current,
    })

    previousRouteRef.current = route
    previousPathnameRef.current = pathname
    hasLoggedRef.current = true

    logRouteActivityWithRetry({ payload, retryTimeoutsRef })
  }, [enabled, location?.pathname, location?.search, location?.hash])
}
